import { useSyncExternalStore } from 'react';

// Kleine store zonder afhankelijkheden: één waarde, abonnees, en een hook met selector.
// Elke functie van de app heeft zo'n store in client/src/state/.

export interface Store<T> {
  get(): T;
  set(next: T | ((prev: T) => T)): void;
  patch(partial: Partial<T>): void;
  subscribe(listener: () => void): () => void;
}

export function createStore<T>(initial: T): Store<T> {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next) {
      const v = typeof next === 'function' ? (next as (prev: T) => T)(value) : next;
      if (Object.is(v, value)) return;
      value = v;
      for (const l of [...listeners]) l();
    },
    patch(partial) {
      this.set({ ...value, ...partial });
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
}

export function useStore<T, S = T>(store: Store<T>, selector: (v: T) => S = (v) => v as unknown as S): S {
  return useSyncExternalStore(store.subscribe, () => selector(store.get()), () => selector(store.get()));
}

/** Bewaart een store in localStorage (alleen wat via `pick` wordt gekozen); leest bij het maken terug. */
export function persist<T>(store: Store<T>, key: string, pick: (v: T) => Partial<T>, restore: (saved: Partial<T>, current: T) => T): void {
  try {
    const raw = localStorage.getItem(key);
    if (raw) store.set((cur) => restore(JSON.parse(raw) as Partial<T>, cur));
  } catch {
    // niets bewaard of niet leesbaar
  }
  store.subscribe(() => {
    try {
      localStorage.setItem(key, JSON.stringify(pick(store.get())));
    } catch {
      // opslag vol of niet beschikbaar
    }
  });
}
